/**
 * Base API client with automatic Clerk token injection.
 * Uses fetch with auth headers from Clerk's useAuth hook.
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8001";

export class ApiError extends Error {
  status: number;
  response?: unknown;

  constructor(status: number, message: string, response?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.response = response;
  }
}

export interface ApiClientOptions {
  getToken: () => Promise<string | null>;
}

/**
 * Create an API client bound to a Clerk session.
 *
 * @example
 * const { getToken } = useAuth();
 * const api = createApiClient({ getToken });
 * const projects = await api.get('/api/projects');
 */
export function createApiClient(options: ApiClientOptions) {
  const { getToken } = options;

  async function request<T = unknown>(
    endpoint: string,
    init?: RequestInit
  ): Promise<T> {
    const token = await getToken();
    if (!token) {
      throw new ApiError(401, "Not authenticated");
    }

    const url = `${API_BASE_URL}${endpoint}`;
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...init?.headers,
    };

    const response = await fetch(url, {
      ...init,
      headers,
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData: unknown;

      try {
        errorData = await response.json();
        if (typeof errorData === "object" && errorData !== null && "detail" in errorData) {
          errorMessage = String(errorData.detail);
        }
      } catch {
        // If response body isn't JSON, use status text
      }

      throw new ApiError(response.status, errorMessage, errorData);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  return {
    get: <T = unknown>(endpoint: string, init?: RequestInit) =>
      request<T>(endpoint, { ...init, method: "GET" }),

    post: <T = unknown>(endpoint: string, body?: unknown, init?: RequestInit) =>
      request<T>(endpoint, {
        ...init,
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      }),

    patch: <T = unknown>(endpoint: string, body?: unknown, init?: RequestInit) =>
      request<T>(endpoint, {
        ...init,
        method: "PATCH",
        body: body ? JSON.stringify(body) : undefined,
      }),

    put: <T = unknown>(endpoint: string, body?: unknown, init?: RequestInit) =>
      request<T>(endpoint, {
        ...init,
        method: "PUT",
        body: body ? JSON.stringify(body) : undefined,
      }),

    delete: <T = unknown>(endpoint: string, init?: RequestInit) =>
      request<T>(endpoint, { ...init, method: "DELETE" }),
  };
}
